import { User } from 'types/User';
import LanguageRegionCode, { HandleLanguageChange } from '@alltrails/shared/types/LanguageRegionCode';
import { useEffect, useState } from 'react';
import Logo from '@alltrails/shared/denali/components/Logo';
import { useIntl, FormattedMessage } from '@alltrails/shared/react-intl';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import LanguageSelectDropdown from '@alltrails/shared/components/LanguageSelectDropdown';
import * as styles from './styles/styles.module.scss';
import MembersForThePlanet from './MembersForThePlanet';
import AppForOutdoors from './AppForOutdoors';
import ConnectWithUs from './ConnectWithUs';
import ExpandableFooterLinks from './ExpandableFooterLinks';
import FooterLinks from './FooterLinks';
import footerLinks from '../../utils/generators/footer_links';
import type FooterLinksType from '../../utils/generators/FooterLinksType';

type FooterProps = {
  enabledLanguages: LanguageRegionCode[];
  user?: User;
  wideFooter: boolean;
  handleLanguageChange: HandleLanguageChange;
  languageRegionCode: LanguageRegionCode;
  noFollowSocials?: boolean;
  noReferrerAppLinks?: boolean;
  irclickid?: string;
};

const Footer = ({
  enabledLanguages,
  user = undefined,
  wideFooter,
  handleLanguageChange,
  languageRegionCode = LanguageRegionCode.English,
  noFollowSocials = true,
  noReferrerAppLinks = true,
  irclickid
}: FooterProps) => {
  const intl = useIntl();
  const linkData = footerLinks(intl, languageRegionCode, user, irclickid);
  const [isOsanoAvailable, setIsOsanoAvailable] = useState(false);

  useEffect(() => {
    if (window.Osano?.cm?.mode === 'production' || window.Osano?.cm?.mode === 'permissive') {
      setIsOsanoAvailable(true);
    }
  }, []);

  const handleManageCookiesClick = () => {
    window.Osano.cm.showDrawer('osano-cm-dom-info-dialog-open');
  };

  return (
    <footer className={`${styles.container} ${wideFooter ? styles.wideContainer : ''}`}>
      <div className={`${styles.innerContainer} ${wideFooter ? styles.wideInnerContainer : ''}`}>
        <Logo size="sm" color="colored-light" variant="logomark" />
        <nav className={styles.nav}>
          <div className={styles.expandableContainer}>
            {linkData.map((link: FooterLinksType) => (
              <ExpandableFooterLinks key={link.title} href={link.href} childLinks={link.childLinks} title={link.title} testId={link.testId} />
            ))}
          </div>
          <div className={styles.footerLinksContainer}>
            {linkData.map((link: FooterLinksType) => (
              <FooterLinks key={link.title} href={link.href} childLinks={link.childLinks} title={link.title} testId={link.testId} />
            ))}
          </div>
        </nav>
        <div className={styles.panelsContainer}>
          <div className={styles.appForOutdoors}>
            <AppForOutdoors languageRegionCode={languageRegionCode} noReferrerAppLinks={noReferrerAppLinks} />
          </div>
          <div className={styles.membersContainer}>
            <MembersForThePlanet />
          </div>
          <div className={styles.connectWithUs}>
            <ConnectWithUs noFollowSocials={noFollowSocials} />
          </div>
        </div>
        <div className={styles.bottomText}>
          <div className={styles.footNotes}>
            <div>
              <div className={styles.copyright}>
                <FormattedMessage defaultMessage="© 2010-{year} AllTrails, LLC" values={{ year: new Date().getFullYear() }} />
              </div>
              <div>
                <FormattedMessage defaultMessage="ALLTRAILS® and the AllTrails Mountain Design are registered trademarks of AllTrails, LLC in the United States as well as certain other jurisdictions." />
              </div>
              <div>
                <FormattedMessage defaultMessage="FIND YOUR OUTSIDE™ is a trademark of AllTrails, LLC." />
              </div>
            </div>
          </div>
          <div className={styles.languageSelect}>
            <LanguageSelectDropdown
              languageRegionCode={languageRegionCode}
              enabledLanguages={enabledLanguages}
              handleLanguageChange={handleLanguageChange}
            />
          </div>
        </div>
        <div className={styles.userLinks}>
          <a href={wrapUrlSafe('/privacy', languageRegionCode)} rel="nofollow">
            <FormattedMessage defaultMessage="Privacy Policy" />
          </a>
          •
          <a href={wrapUrlSafe('/terms', languageRegionCode)} rel="nofollow">
            <FormattedMessage defaultMessage="Terms" />
          </a>
          •
          <a href={wrapUrlSafe('/cookie_policy', languageRegionCode)} rel="nofollow">
            <FormattedMessage defaultMessage="Cookie Policy" />
          </a>
          {isOsanoAvailable && (
            <>
              •
              <button type="button" onClick={handleManageCookiesClick} className={styles.manageCookiesBtn}>
                <FormattedMessage defaultMessage="Manage Cookies" />
              </button>
            </>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
